/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import moment, {Moment as MomentType} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {Vehicle, VehicleEngineeringMode} from '@ion/core/src/types/entities';
import {
  DATE_RANGE_FORMAT,
  getCurrentDate
} from '@ion/core/src/utils/DateUtil';
import Alert from '@ion/dls/src/components/alert/Alert';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import RefreshGridButton from '@ion/dls/src/components/buttons/RefreshGridButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import IonDialog from '@ion/dls/src/components/dialogs/IonDialog';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import useLoadVehicleHistory from '@ion/query/src/hooks/useLoadVehicleHistory';
import useVehicleMqttConnectionStatus from '@ion/query/src/hooks/useVehicleMqttConnectionStatus';
import VehicleEngineeringModeCell from '../cells/VehicleEngineeringModeCell';
import {
  canRequestsViewMode,
  selectedVehicle,
  setCanRequestsViewMode
} from '../manager/store/store';
import VehicleCanRequestsTab from './VehicleCanRequestsTab';
import VehicleHistoryGrid from './VehicleHistoryGrid';

export interface VehicleDetailsProps {
  onClose: (show: boolean) => void;
  vehicleHistoryTitle?: string;
  vinId: string | null;
  initialTab?: VehicleDetailsTab;
  canRequestsVisible?: boolean;
  canRequestEnabled?: boolean;
  canViewAuditLog?: boolean;
}

interface SelectedVehicle extends Vehicle {
  year: string;
  make: string;
  model: string;
}

export enum VehicleDetailsTab {
  VehicleIdReport = 'vehicleIdReport',
  CanRequests = 'canRequests'
}

type PickerDate = Date | MomentType | string | null;

const getDefaultStartDate = (): Date => (
  moment().subtract(1, 'months').startOf('day').toDate()
);

const useStyles = makeStyles((theme: DLSTheme) => ({
  gridContainer : {
    width           : '100%',
    backgroundColor : dlsColor('white'),
    padding         : '24px 24px 16px',
    borderRadius    : '2px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label2'),
    color    : dlsColor('grey2'),
    fontSize : '14px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label3'),
    color        : dlsColor('grey1'),
    fontWeight   : 700,
    fontSize     : '14px',
    textOverflow : 'ellipsis',
    overflow     : 'hidden',
    padding      : '8px 0px'
  },
  modeBodyItem : {
    ...dlsAlt('typography', 'label3'),
    color      : dlsColor('grey1'),
    fontWeight : 700,
    fontSize   : '14px',
    padding    : '8px 0px',
    marginLeft : '-5px',
    minWidth   : '250px'
  },
  contentContainer : {
    width : '100%'
  },
  flexStyle : {
    display                        : 'flex',
    margin                         : '20px 0',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexWrap : 'wrap'
    }
  },
  dropdownStyle : {
    width                          : '200px',
    marginRight                    : '10px',
    [theme.breakpoints.down('sm')] : {
      width       : '48%',
      marginRight : '5px'
    }
  },
  eventDropdownStyle : {
    width                          : '200px',
    [theme.breakpoints.down('sm')] : {
      width : '170px'
    }
  },
  searchContainer : {
    marginRight : '10px',
    marginTop   : '26px'
  },
  iconStyle : {
    display     : 'flex',
    marginLeft  : 'auto',
    marginRight : '10px',
    marginTop   : '25px'
  },
  canRequestsIconStyle : {
    display     : 'flex',
    marginLeft  : 'auto',
    marginRight : '10px',
    marginTop   : '0'
  },
  customTab : {
    color                  : dlsColor('grey1'),
    letterSpacing          : '1px',
    '& .MuiTabs-indicator' : {
      backgroundColor : dlsColor('grey1'),
      height          : '4px'
    },
    '& .MuiTab-root' : {
      ...dlsAlt('typography', 'label1'),
      color        : dlsColor('grey1'),
      fontWeight   : 600,
      minWidth     : 'auto',
      padding      : '0px 10px',
      marginBottom : '-4px',
      transition   : 'color 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      '&:hover'    : {
        color : dlsColor('black')
      }
    },
    '& .MuiTab-wrapper' : {
      marginBottom : '-10px'
    }
  }
}));

/**
 * Display View VIN ID History on click of VIN value from grid menu
 */
export const VehicleDetailsEditor = ({
  onClose,
  vinId,
  vehicleHistoryTitle,
  initialTab = VehicleDetailsTab.VehicleIdReport,
  canRequestsVisible = false,
  canRequestEnabled = false,
  canViewAuditLog = false
}: VehicleDetailsProps) => {
  const classes                                 = useStyles();
  const translations                            = 'vehicle-manager:vehicleHistoryGrid';
  const [activeTab, setActiveTab]               = useState<VehicleDetailsTab>(initialTab);
  const [openDatePicker, setOpenDatePicker]     = useState<boolean>(false);
  const [draftStartDate, setDraftStartDate]     = useState<PickerDate>(getDefaultStartDate);
  const [draftEndDate, setDraftEndDate]         = useState<PickerDate>(getCurrentDate);

  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(
    () => moment(getDefaultStartDate()).format(DATE_RANGE_FORMAT)
  );
  const [appliedEndDate, setAppliedEndDate]     = useState<string | null>(
    () => moment(getCurrentDate()).format(DATE_RANGE_FORMAT)
  );

  const onDialogClose = useCallback(() => {
    setCanRequestsViewMode?.('view');
    onClose?.(false);
  }, [onClose]);

  const canRequestsAllowed = canRequestsVisible && canRequestEnabled;

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const toggleDatePicker = useCallback(() => {
    setOpenDatePicker(true);
    setTimeout(() => {
      setOpenDatePicker(false);
    }, 1000);
  }, []);

  const {
    vehicleHistory = [],
    refetchHistory,
    refresh: onVehicleHistoryRefresh,
    isFetching: isFetchingVehicleHistory
  } = useLoadVehicleHistory({
    vinId     : vinId as string,
    startDate : appliedStartDate,
    endDate   : appliedEndDate
  });

  const onSearch = useCallback(() => {
    const formattedStart = draftStartDate ? moment(draftStartDate).format(DATE_RANGE_FORMAT) : null;
    const formattedEnd   = draftEndDate ? moment(draftEndDate).format(DATE_RANGE_FORMAT) : null;
    if (formattedStart === appliedStartDate && formattedEnd === appliedEndDate) {
      refetchHistory?.();
      return;
    }
    setAppliedStartDate(formattedStart);
    setAppliedEndDate(formattedEnd);
  }, [draftStartDate, draftEndDate, appliedStartDate, appliedEndDate, refetchHistory]);

  const onTabChange = useCallback((event: SyntheticEvent, value: VehicleDetailsTab) => {
    setActiveTab(value);
  }, []);

  const {
    year = '',
    make = '',
    model = ''
  } = (selectedVehicle.value as SelectedVehicle) || {};

  const rawEsn      = selectedVehicle.value?.esn;
  const dialogEsns  = useMemo(() => {
    const first = Array.isArray(rawEsn) ? rawEsn[0] : rawEsn;
    return first ? [first] : [];
  }, [rawEsn]);

  const {connectionStatusMap} = useVehicleMqttConnectionStatus(dialogEsns);
  const dialogIsConnected     = connectionStatusMap[dialogEsns[0]] ?? false;

  const isCreatingCanRequest       = canRequestsViewMode.value === 'create';
  const isViewingCanRequestJson    = canRequestsViewMode.value === 'json';
  const isViewingCanRequestDetails = canRequestsViewMode.value === 'details';

  const titleFromVehicleHistory = useMemo(() => {
    const {
      vin,
      yearString,
      makeString,
      modelString
    } = vehicleHistory?.[0]?.vehicleIdHistory || {};
    return !isUndefined(vin) ? `${vin} - ${yearString} ${makeString} ${modelString}` : '';
  }, [vehicleHistory]);

  return (
    <IonDialog open
      isMaxSize
      title={vehicleHistoryTitle || titleFromVehicleHistory || translate(`${translations}.fallbackTitle`)}
      onClose={onDialogClose}
      actions={(
        <TertiaryButton onClick={onDialogClose}>
          {translate('common:close')}
        </TertiaryButton>
      )}>
      <Render.If condition={!isViewingCanRequestDetails && !isCreatingCanRequest && !isViewingCanRequestJson}>
        <div className={classes.contentContainer}>
          <Tabs className={classes.customTab}
            onChange={onTabChange}
            value={activeTab}>
            <Tab value={VehicleDetailsTab.VehicleIdReport}
              label={translate(`${translations}.tab`)} />
            {canRequestsAllowed
              ? (
                <Tab value={VehicleDetailsTab.CanRequests}
                  label={translate(`${translations}.canRequestsTab`)} />
              )
              : null}
          </Tabs>
        </div>
      </Render.If>
      <Render.If condition={activeTab === VehicleDetailsTab.VehicleIdReport}>
        <Render.If condition={isEmpty(vehicleHistory)}>
          <Alert type="neutral"
            title={translate(`${translations}.alert.title`)}
            message={translate(`${translations}.alert.message`)}
            gutters={false} />
        </Render.If>
        <Render.If condition={!isEmpty(vehicleHistory)}>
          <Grid container
            spacing={0}
            alignItems='flex-start'
            justifyContent='flex-start'
            className={classes.gridContainer}>
            <Grid item md={2}>
              <div className={classes.headerItem}>
                {translate(`${translations}.year`)}
              </div>
              <div className={classes.bodyItem}>
                {year || vehicleHistory?.[0]?.vehicleIdHistory.yearString}
              </div>
            </Grid>
            <Grid item md={2}>
              <div className={classes.headerItem}>
                {translate(`${translations}.make`)}
              </div>
              <div className={classes.bodyItem}>
                {make || vehicleHistory?.[0]?.vehicleIdHistory.makeString}
              </div>
            </Grid>
            <Grid item md={2}>
              <div className={classes.headerItem}>
                {translate(`${translations}.model`)}
              </div>
              <div className={classes.bodyItem}>
                {model || vehicleHistory?.[0]?.vehicleIdHistory.modelString}
              </div>
            </Grid>
            <Grid item md={2}>
              <div className={classes.headerItem}>
                {translate(`${translations}.mode`)}
              </div>
              <div className={classes.modeBodyItem}>
                <VehicleEngineeringModeCell mode={selectedVehicle.value?.veMode as VehicleEngineeringMode}
                  pendingMode={selectedVehicle.value?.pendingVEMode}
                  isConnected={dialogIsConnected} />
              </div>
            </Grid>
          </Grid>
        </Render.If>
        <div className={classes.flexStyle}>
          <div className={classes.dropdownStyle}>
            <DatePicker closeOnClickOutside
              useDatePickerTextField
              maxDate={new Date(getCurrentDate())}
              hideClearButton
              fieldLabel={translate(`${translations}.startDate`)}
              toggle={toggleDatePicker}
              defaultValue={getDefaultStartDate()}
              onChange={setDraftStartDate} />
          </div>
          <div className={classes.dropdownStyle}>
            <DatePicker closeOnClickOutside
              useDatePickerTextField
              maxDate={new Date(getCurrentDate())}
              hideClearButton
              fieldLabel={translate(`${translations}.endDate`)}
              toggle={toggleDatePicker}
              defaultValue={getCurrentDate() as string | Date}
              onChange={setDraftEndDate} />
          </div>
          <div className={classes.searchContainer}>
            <PrimaryButton onClick={onSearch}>
              {translate(`${translations}.searchButton`)}
            </PrimaryButton>
          </div>
          <div className={classes.iconStyle}>
            <RefreshGridButton onRefresh={onVehicleHistoryRefresh} />
          </div>
        </div>
        <VehicleHistoryGrid vehicleHistoryData={vehicleHistory} loading={isFetchingVehicleHistory} />
      </Render.If>
      <Render.If condition={canRequestsAllowed && activeTab === VehicleDetailsTab.CanRequests}>
        <VehicleCanRequestsTab canRequestEnabled={canRequestsAllowed}
          canViewAuditLog={canViewAuditLog} />
      </Render.If>
    </IonDialog>
  );
};

export default VehicleDetailsEditor;
